<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// Card "Plantão de suporte" (FE-AUD-55) do "Histórico de cliente".
// Extraído de HistoricoCliente.vue preservando gates de permissão, estados e copy.
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LeadHistoryState from './LeadHistoryState.vue';
import { SUPPORT_UPDATE_OPTIONS } from '../utils/leadHistoryConstants';
import {
  normalizeSupportOrigin,
  normalizeSupportQuestion,
  supportNextActionLabel,
  supportStatus,
  supportStatusMeta,
} from '../utils/leadHistoryPresenter';

const props = defineProps({
  support: { type: Object, default: null },
  loadError: { type: Boolean, default: false },
  canUpdate: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  reason: { type: String, default: '' },
  nextStatus: { type: String, default: 'complete' },
});

defineEmits([
  'retry',
  'update:reason',
  'update:nextStatus',
  'submit',
  'resolve',
  'openConversation',
]);

const pendingSupportStatus = computed(() => supportStatus(props.support));
const pendingSupportStatusMeta = computed(() =>
  supportStatusMeta(props.support)
);
const pendingSupportQuestionLabel = computed(() =>
  normalizeSupportQuestion(props.support)
);
const pendingSupportQuestionAnswer = computed(
  () => props.support?.duty?.support_question?.answer || ''
);
const pendingSupportOriginLabel = computed(() =>
  normalizeSupportOrigin(props.support)
);
const pendingSupportResponsibleLabel = computed(
  () => props.support?.assignee?.name || 'Sem responsável'
);
const pendingSupportNextAction = computed(() =>
  supportNextActionLabel(props.support, props.canUpdate)
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Plantão de suporte">
    <template #titleAction>
      <span
        v-if="support"
        class="inline-flex rounded-full px-2 py-1 text-[11px] font-semibold"
        :class="pendingSupportStatusMeta.tone"
      >
        {{ pendingSupportStatusMeta.label }}
      </span>
    </template>

    <LeadHistoryState
      v-if="loadError"
      title="Plantão indisponível"
      description="Não foi possível consultar o suporte pendente deste cliente."
      action-label="Tentar novamente"
      @action="$emit('retry')"
    />

    <div v-else-if="support" class="grid gap-4">
      <div class="grid gap-3 md:grid-cols-2">
        <LaisInfoRow icon="i-lucide-message-help" label="Pergunta">
          {{ pendingSupportQuestionLabel }}
          <span
            v-if="pendingSupportQuestionAnswer"
            class="mt-1 block text-[12px] font-normal text-lais-text-body"
          >
            Resposta: {{ pendingSupportQuestionAnswer }}
          </span>
        </LaisInfoRow>
        <LaisInfoRow icon="i-lucide-radio" label="Origem">
          {{ pendingSupportOriginLabel }}
        </LaisInfoRow>
        <LaisInfoRow icon="i-lucide-user-round" label="Responsável">
          {{ pendingSupportResponsibleLabel }}
        </LaisInfoRow>
        <LaisInfoRow icon="i-lucide-list-checks" label="Próxima ação">
          {{ pendingSupportNextAction }}
        </LaisInfoRow>
      </div>

      <div
        v-if="pendingSupportStatus !== 'complete'"
        class="grid gap-3 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-3"
      >
        <p
          v-if="!canUpdate"
          class="rounded-lais-sm border border-amber-100 bg-amber-50 p-3 text-[12px] font-semibold text-amber-800"
        >
          Ação bloqueada: seu usuário não possui permissão para atualizar este
          plantão.
        </p>
        <div class="grid gap-2 md:grid-cols-[220px_1fr]">
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Status
            <select
              :value="nextStatus"
              class="h-10 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!canUpdate || isUpdating"
              @change="$emit('update:nextStatus', $event.target.value)"
            >
              <option
                v-for="option in SUPPORT_UPDATE_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Motivo
            <textarea
              :value="reason"
              rows="2"
              class="min-h-[40px] rounded-lais-sm border border-[#e5e7eb] bg-white px-3 py-2 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
              placeholder="Registre o motivo da atualização"
              :disabled="!canUpdate || isUpdating"
              @input="$emit('update:reason', $event.target.value)"
            />
          </label>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!canUpdate || isUpdating"
            @click="$emit('submit')"
          >
            <span class="i-lucide-save size-4" aria-hidden="true" />
            Atualizar suporte
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais bg-lais-primary px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!canUpdate || isUpdating"
            @click="$emit('resolve')"
          >
            <span class="i-lucide-check-circle-2 size-4" aria-hidden="true" />
            Resolver suporte
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc]"
            @click="$emit('openConversation')"
          >
            <span class="i-lucide-message-circle size-4" aria-hidden="true" />
            Abrir conversa
          </button>
        </div>
      </div>

      <p
        v-else
        class="rounded-lais-sm border border-emerald-100 bg-emerald-50 p-3 text-[12px] font-semibold text-emerald-800"
      >
        Atendimento resolvido e registrado na trilha privada da conversa.
      </p>
    </div>
  </LaisCard>
</template>
